import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import SiteService from "../../service/SiteService.mjs";
import SiteActions from "../../actions/SiteActions";
import UserActions from "../../actions/UserActions";
import {
  RENDERER,
  RENDERER_LOCK_ELIGIBILITY,
  USER_SITES,
} from "../../constants/QueryKeys";

/**
 * @typedef {import("../../constants/RendererConstants.mjs").RendererTypeValue} RendererTypeValue
 * @typedef {import("../../service/SiteService.mjs").RendererLockEligibility} RendererLockEligibility
 */

const defaults = {
  refetchOnWindowFocus: false,
  refetchOnReconnect: false,
  staleTime: Infinity,
  retry: false,
};

/**
 * @typedef {import("@/service/SiteService.mjs").LockResult} LockResult
 * @typedef {{ renderer: RendererTypeValue, canToggle: boolean }} RendererState
 *
 * @typedef {Object} UseRendererLockResult
 * @prop {RendererLockEligibility|undefined} eligibility - undefined until the first fetch resolves
 * @prop {boolean} eligibilityLoading
 * @prop {unknown} eligibilityError - non-null when the fetch failed (network / 5xx); callers should surface a fallback UI
 * @prop {() => void} lock - fires the lock mutation; watch lockResult/lockCompleted/lockError for state
 * @prop {LockResult|undefined} lockResult - discriminated on `locked`; undefined until the mutation completes
 * @prop {boolean} isLocking
 * @prop {unknown} lockError - genuine transport / 5xx failures only (the not-locked variant is a success)
 * @prop {boolean} lockCompleted - true once the mutation resolves either variant
 * @prop {() => void} resetLock - clear mutation state (use when opening a fresh confirm dialog)
 */

/**
 * Reads eligibility for the one-way v2 renderer lock and exposes a lock mutation.
 *
 * The server returns a discriminated variant: `{ locked: true, renderer, canToggle }`
 * or `{ locked: false, eligibility }`. The hook writes the appropriate cache on
 * each branch (RENDERER on locked, RENDERER_LOCK_ELIGIBILITY on not-locked) and
 * passes the raw variant back on `lockResult` so consumers can branch.
 *
 * Eligibility is gated by `enabled` so it only runs where the UI needs it
 * (e.g. when Site Settings → Publish is open).
 *
 * @param {number} siteId
 * @param {{ enabled?: boolean }} [options]
 * @returns {UseRendererLockResult}
 */
const useRendererLock = (siteId, { enabled = true } = {}) => {
  const queryClient = useQueryClient();

  const {
    data: eligibility,
    isLoading: eligibilityLoading,
    error: eligibilityError,
  } = useQuery({
    queryKey: [RENDERER_LOCK_ELIGIBILITY, siteId],
    queryFn: () => SiteService.getRendererLockEligibility(siteId),
    enabled: enabled && siteId != null,
    ...defaults,
  });

  const { mutate, data, isPending, isSuccess, error, reset } = useMutation({
    mutationFn: () => SiteService.lockRenderer(siteId),
    onSuccess: ({ locked, renderer, canToggle, eligibility } = {}) => {
      if (locked) {
        queryClient.setQueryData([RENDERER, siteId], { renderer, canToggle });
        queryClient.invalidateQueries({ queryKey: [USER_SITES] });
        queryClient.invalidateQueries({
          queryKey: [RENDERER_LOCK_ELIGIBILITY, siteId],
        });
        SiteActions.designLocked();
        UserActions.trackPostHogEvent("renderer_locked", { actor: "customer" });
      } else {
        queryClient.setQueryData(
          [RENDERER_LOCK_ELIGIBILITY, siteId],
          eligibility,
        );
      }
    },
  });

  return {
    eligibility,
    eligibilityLoading,
    eligibilityError,
    lock: mutate,
    lockResult: data,
    isLocking: isPending,
    lockError: error,
    lockCompleted: isSuccess,
    resetLock: reset,
  };
};

export default useRendererLock;
